/**
 * RegistrationPaymentsPanel — every fee paid for a tournament (players, team
 * owners, sponsors, team fees) and the refunds made against them.
 *
 * Refunds can be full or partial, any number of times up to what was paid.
 * "Refund via Razorpay" (money goes back to the payer's original method) is
 * Super Admin only; the event's admins can record a refund made outside the
 * app. "Refund due" only flags paid items whose registration or team was
 * rejected/withdrawn or whose tournament was cancelled — nothing is automatic.
 * Backed by /v1/admin/events/{id}/payments.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance, fmtDT, getApiErrorMessage, Modal } from './_shared';

const inr = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const METHOD_LABEL = {
  razorpay: 'Razorpay',
  razorpay_dashboard: 'Razorpay (dashboard)',
  offline: 'Outside the app',
};

function PaymentStatusBadge({ item }) {
  if (item.refunded >= item.paid) {
    return <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Refunded</span>;
  }
  if (item.refunded > 0) {
    return <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Partly refunded</span>;
  }
  return <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-600">Paid</span>;
}

function RefundModal({ eventId, item, canRazorpay, onClose, onDone }) {
  const razorpayOk = canRazorpay && item.has_razorpay_payment;
  const [method, setMethod] = useState(razorpayOk ? 'razorpay' : 'offline');
  const [amount, setAmount] = useState(String(item.refundable / 100));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const paise = Math.round(parseFloat(amount || '0') * 100);
  const valid = paise > 0 && paise <= item.refundable && reason.trim().length >= 3;

  const submit = async () => {
    const what = method === 'razorpay'
      ? `Send ${inr(paise)} back to ${item.payer.name || 'the payer'} through Razorpay? This can't be undone.`
      : `Record that ${inr(paise)} was refunded to ${item.payer.name || 'the payer'} outside the app?`;
    if (!window.confirm(what)) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/v1/admin/events/${eventId}/payments/${item.kind}/${item.id}/refunds`, {
        amount: paise, reason: reason.trim(), method,
      });
      toast.success(method === 'razorpay' ? `Refund of ${inr(paise)} sent` : `Refund of ${inr(paise)} recorded`);
      onDone();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Refund failed'));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white';
  return (
    <Modal title="Refund" onClose={onClose}>
      <div className="space-y-4" data-testid="refund-modal">
        <div className="text-sm">
          <p className="font-bold">{item.payer.name || item.payer.email}</p>
          <p className="text-xs text-gray-500">{item.label} · paid {inr(item.paid)}
            {item.refunded > 0 && <> · refunded {inr(item.refunded)}</>} · <strong>{inr(item.refundable)}</strong> left</p>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">How</p>
          {canRazorpay && (
            <label className={`flex items-start gap-2 p-3 rounded-xl border-2 ${method === 'razorpay' ? 'border-[#2B3A67]' : 'border-gray-100'} ${razorpayOk ? 'cursor-pointer' : 'opacity-50'}`}>
              <input type="radio" name="refund-method" checked={method === 'razorpay'} disabled={!razorpayOk}
                onChange={() => setMethod('razorpay')} className="mt-1" data-testid="refund-method-razorpay" />
              <span>
                <span className="font-bold text-sm block">Refund via Razorpay</span>
                <span className="text-xs text-gray-500">
                  {razorpayOk ? 'Money goes back to the payer\'s original payment method.' : 'No Razorpay payment on record for this item.'}
                </span>
              </span>
            </label>
          )}
          <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${method === 'offline' ? 'border-[#2B3A67]' : 'border-gray-100'}`}>
            <input type="radio" name="refund-method" checked={method === 'offline'}
              onChange={() => setMethod('offline')} className="mt-1" data-testid="refund-method-offline" />
            <span>
              <span className="font-bold text-sm block">Refunded outside the app</span>
              <span className="text-xs text-gray-500">Cash or direct transfer — this only records it.</span>
            </span>
          </label>
          {!canRazorpay && (
            <p className="text-xs text-gray-400">Refunds through Razorpay are made by a Super Admin.</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 mb-1">Amount (₹)</label>
          <input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            className={inputCls} data-testid="refund-amount" />
          {paise > item.refundable && <p className="text-xs text-red-500 mt-1">At most {inr(item.refundable)}</p>}
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 mb-1">Reason *</label>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Shown to the payer" className={inputCls} data-testid="refund-reason" />
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 bg-gray-200 text-gray-700 font-bold rounded-2xl">Cancel</button>
          <button onClick={submit} disabled={!valid || busy}
            className="flex-1 py-3 bg-[#E54562] text-white font-extrabold rounded-2xl disabled:opacity-50"
            data-testid="refund-submit">
            {busy ? 'Working…' : method === 'razorpay' ? `Refund ${paise > 0 ? inr(paise) : ''}` : 'Record refund'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function RegistrationPaymentsPanel({ event }) {
  const [data, setData] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [dueOnly, setDueOnly] = useState(false);

  const load = useCallback(async () => {
    try { setData((await axiosInstance.get(`/v1/admin/events/${event.id}/payments`)).data); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not load payments')); setData({ items: [], totals: {} }); }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E54562]" /></div>;
  }
  const items = dueOnly ? data.items.filter((i) => i.refund_due) : data.items;

  return (
    <div className="space-y-3" data-testid="payments-panel">
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
          <p className="text-lg font-extrabold">{inr(data.totals.paid)}</p><p className="text-xs text-gray-400">Collected</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <p className="text-lg font-extrabold text-gray-700">{inr(data.totals.refunded)}</p><p className="text-xs text-gray-500">Refunded</p>
        </div>
        <button onClick={() => setDueOnly(!dueOnly)}
          className={`rounded-xl p-3 text-center ${dueOnly ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700'}`}
          data-testid="refund-due-filter">
          <p className="text-lg font-extrabold">{data.totals.refund_due || 0}</p><p className="text-xs">Refund due</p>
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><p className="text-sm">{dueOnly ? 'No refunds due' : 'No payments yet'}</p></div>
      ) : items.map((it) => (
        <div key={`${it.kind}-${it.id}`} className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm"
          data-testid={`payment-${it.kind}-${it.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-bold text-sm truncate">{it.payer.name || it.payer.email}</p>
              <p className="text-xs text-gray-400 truncate">{it.label}{it.paid_at ? ` · ${fmtDT(it.paid_at)}` : ''}</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <PaymentStatusBadge item={it} />
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#2B3A67]/5 text-[#2B3A67]">
                  {inr(it.paid)} paid{it.refunded > 0 && ` · ${inr(it.refunded)} refunded`}
                </span>
                {it.refund_due && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                    data-testid={`refund-due-${it.id}`}>Refund due · {it.refund_due_reason}</span>
                )}
              </div>
            </div>
            {it.refundable > 0 && (
              <button onClick={() => setRefunding(it)}
                className="px-3 py-1.5 rounded-lg border-2 border-[#E54562] text-[#E54562] text-xs font-bold shrink-0"
                data-testid={`refund-btn-${it.id}`}>Refund</button>
            )}
          </div>
          {it.refunds.length > 0 && (
            <ul className="mt-2 pt-2 border-t border-gray-100 space-y-1">
              {it.refunds.map((r) => (
                <li key={r.id} className="text-xs text-gray-500 flex flex-wrap gap-x-2">
                  <span className="font-bold text-gray-700">−{inr(r.amount)}</span>
                  <span>{METHOD_LABEL[r.method] || r.method}{r.status === 'pending' ? ' (processing)' : ''}</span>
                  {r.at && <span>{fmtDT(r.at)}</span>}
                  {r.by && <span>by {r.by}</span>}
                  {r.reason && <span className="italic truncate">“{r.reason}”</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {refunding && (
        <RefundModal eventId={event.id} item={refunding} canRazorpay={data.can_refund_via_razorpay}
          onClose={() => setRefunding(null)} onDone={() => { setRefunding(null); load(); }} />
      )}
    </div>
  );
}
